import { MAX_UPLOAD_FILE_BYTES, MAX_UPLOAD_TOTAL_BYTES, type SelectedFile } from "./folder";

interface PublishResultBase {
  projectId: string;
  url: string;
  revision: string;
  activation?: "pending_review";
  customHttp?: { manifestVersionId: string; digest: string; status: "pending_review" };
  sourceRuntime?: {
    framework: "next-managed-runtime";
    runtimeId: string;
    connections: Array<{ alias: string; label: string; environmentVariable: string }>;
  };
}

export type PublishResult = PublishResultBase & (
  | {
      apiMode: "discovered";
      apiDiscovery: {
        status: "pending_review";
        projectId: string;
        revisionId: string;
      };
    }
  | { apiMode: "portable_manifest"; apiDiscovery?: never }
);

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  const configured = import.meta.env.VITE_CONTROL_API_URL as string | undefined;
  const base = configured?.replace(/\/$/, "") ?? "";
  const response = await fetch(`${base}${path}`, {
    credentials: "same-origin",
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? "Publishing stopped before your live app changed. Try again.");
  }
  return response;
}

export async function publishFiles(
  files: SelectedFile[],
  project: { projectId?: string; workspaceId?: string; visibility?: "public" | "password"; password?: string } = {},
): Promise<PublishResult> {
  let totalBytes = 0;
  for (const { file, path } of files) {
    if (file.size > MAX_UPLOAD_FILE_BYTES) throw new Error(`${path} is larger than the 1 MiB Free plan file limit.`);
    totalBytes += file.size;
    if (totalBytes > MAX_UPLOAD_TOTAL_BYTES) throw new Error("This folder is larger than the 5 MiB Free plan limit.");
  }

  const encoded = [];
  for (const { file, path } of files) {
    encoded.push({
      path,
      contentBase64: bytesToBase64(new Uint8Array(await file.arrayBuffer())),
      contentType: file.type || undefined,
    });
  }
  const response = await request("/api/demo/publish", {
    method: "POST",
    body: JSON.stringify({
      files: encoded,
      idempotencyKey: crypto.randomUUID(),
      ...(project.projectId ? { projectId: project.projectId } : {}),
      ...(project.workspaceId ? { workspaceId: project.workspaceId } : {}),
      ...(project.visibility ? { visibility: project.visibility } : {}),
      ...(project.visibility === "password" && project.password ? { password: project.password } : {}),
    }),
  });
  return (await response.json()) as PublishResult;
}

export async function changeAvailability(
  action: "offline" | "reactivate",
  projectId: string,
): Promise<void> {
  await request(`/api/demo/${action}?projectId=${encodeURIComponent(projectId)}`, {
    method: "POST",
    body: "{}",
  });
}
